/**
 * Gift Box Customizer Modal - Two-Up Edition
 * Two-up preview (desktop) + segmented control (mobile) + sidebar editor
 */

(() => {
  // Snowcubes Gift Card Core (loaded globally via `layout/theme.liquid`)
  const Gift = window.SnowcubesGift || {};

  // Card catalog (loaded via gift-card-catalog.js before this file)
  const Catalog = window.SnowcubesGiftCatalog || {};
  const CARD_CATALOG = Catalog.CARD_CATALOG || [];
  const CARD_CATALOG_BY_ID = Catalog.CARD_CATALOG_BY_ID || {};
  const CARD_PICKER_CATALOG = Catalog.CARD_PICKER_CATALOG || CARD_CATALOG.filter((card) => !card.hidden);
  const DEFAULT_CARD_ID = Catalog.DEFAULT_CARD_ID || 'knicks_2026_the_leap';
  const FONT_OPTIONS = Catalog.FONT_OPTIONS || [];
  const DEFAULT_FONT_ID = Catalog.DEFAULT_FONT_ID || 'serif';
  const GREETING_MAX_LENGTH = Catalog.GREETING_MAX_LENGTH || 30;
  const CLOSING_MAX_LENGTH = Catalog.CLOSING_MAX_LENGTH || 20;
  const normalizeFontId = Catalog.normalizeFontId || ((id) => id || DEFAULT_FONT_ID);

  const CARD_IMAGE_WIDTHS = {
    cover: [160, 240, 320, 480],
    front: [240, 320, 480],
    summary: [96, 160],
  };
  const CARD_IMAGE_FALLBACK_WIDTH = {
    cover: 320,
    front: 480,
    summary: 96,
  };
  const CARD_IMAGE_SIZES = {
    cover: '(max-width: 768px) 160px, 220px',
    front: '(max-width: 900px) 320px, 320px',
    summary: '28px',
  };
  const TRANSPARENT_IMAGE_SRC = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';

  function withImageWidth(url, width) {
    if (!url || !width) return url || '';
    try {
      const parsed = new URL(url, window.location.href);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (_) {
      const cleanUrl = url
        .replace(/([?&])width=\d+(&?)/, (match, prefix, suffix) => (suffix ? prefix : ''))
        .replace(/[?&]$/, '');
      return `${cleanUrl}${cleanUrl.includes('?') ? '&' : '?'}width=${width}`;
    }
  }

  function getCardImageUrl(card, variant) {
    const width = CARD_IMAGE_FALLBACK_WIDTH[variant] || CARD_IMAGE_FALLBACK_WIDTH.cover;
    return withImageWidth(card?.imageUrl, width);
  }

  function getCardImageSrcset(card, variant) {
    const widths = CARD_IMAGE_WIDTHS[variant] || CARD_IMAGE_WIDTHS.cover;
    return widths.map((width) => `${withImageWidth(card?.imageUrl, width)} ${width}w`).join(', ');
  }

  function getCardImageAttributes(card, variant, defer = true) {
    if (!card?.imageUrl) return 'src=""';
    if (defer) {
      return [
        `src="${TRANSPARENT_IMAGE_SRC}"`,
        `data-src="${getCardImageUrl(card, variant)}"`,
        `data-srcset="${getCardImageSrcset(card, variant)}"`,
        `data-sizes="${CARD_IMAGE_SIZES[variant] || CARD_IMAGE_SIZES.cover}"`,
        'loading="lazy"',
        'decoding="async"',
        'fetchpriority="low"',
      ].join(' ');
    }
    return [
      `src="${getCardImageUrl(card, variant)}"`,
      `srcset="${getCardImageSrcset(card, variant)}"`,
      `sizes="${CARD_IMAGE_SIZES[variant] || CARD_IMAGE_SIZES.cover}"`,
      'loading="lazy"',
      'decoding="async"',
      'fetchpriority="low"',
    ].join(' ');
  }

  function applyDeferredCardImage(img, card, variant) {
    if (!img || !card?.imageUrl) return;
    img.src = TRANSPARENT_IMAGE_SRC;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.dataset.src = getCardImageUrl(card, variant);
    img.dataset.srcset = getCardImageSrcset(card, variant);
    img.dataset.sizes = CARD_IMAGE_SIZES[variant] || CARD_IMAGE_SIZES.cover;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.setAttribute('fetchpriority', 'low');
  }

  function applyCardImage(img, card, variant) {
    if (!img || !card?.imageUrl) return;
    img.src = getCardImageUrl(card, variant);
    img.srcset = getCardImageSrcset(card, variant);
    img.sizes = CARD_IMAGE_SIZES[variant] || CARD_IMAGE_SIZES.cover;
    delete img.dataset.src;
    delete img.dataset.srcset;
    delete img.dataset.sizes;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.setAttribute('fetchpriority', 'low');
  }

// ── Backwards-compatible fallbacks ──
// These only run when gift-card-core.js hasn't set the globals first.
// In practice, core always loads before this file because both use `defer`
// and core's <script> appears earlier in layout/theme.liquid (line ~39 vs
// the modal snippet at line ~126). So these branches are dead code under
// normal operation — they exist purely as a safety net for edge cases
// (e.g. core fails to load due to a network error).
if (!window.isGiftUIEnabled) {
  // Fallback: delegate to centralized SnowcubesFlags if available.
  window.isGiftUIEnabled = function isGiftUIEnabledFallback() {
    return true;
  };
}

// Fallback giftConfigPrototype — only used when gift-card-core.js fails to
// load (see load-order note above). Dead code under normal operation.
if (!window.giftConfigPrototype) {
  const GIFT_CONFIG_KEY_PREFIX = 'sc_gift_config_prototype';
  const getHandle = () => {
    try {
      const m = location.pathname.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : null;
    } catch (_) { return null; }
  };
  const getKey = () => {
    const h = getHandle();
    return h ? `${GIFT_CONFIG_KEY_PREFIX}:${h}` : GIFT_CONFIG_KEY_PREFIX;
  };

  window.giftConfigPrototype = {
    save(config) {
      try {
        localStorage.setItem(getKey(), JSON.stringify(config));
        return true;
      } catch (e) {
        return false;
      }
    },
    load() {
      try {
        const stored = localStorage.getItem(getKey());
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    },
    clear() {
      try {
        localStorage.removeItem(getKey());
        return true;
      } catch (e) {
        return false;
      }
    }
  };
}

class GiftBoxCustomizerModal extends HTMLElement {
  constructor() {
    super();
    
    this.isOpen = false;
    this.activePreviewSide = 'front';
    this.mode = 'add';
    this.step = 'pick-card'; // 'pick-card' | 'write-message'
    this.variantId = null;
    this.itemKey = null; // Prefer itemKey for cart updates (most stable identifier)

    // Form state
    this.selectedCardId = DEFAULT_CARD_ID;
    this.selectedGreeting = '';
    this.selectedClosing = '';
    this.recipientName = '';
    this.noteText = '';
    this.senderName = '';
    this.selectedFont = DEFAULT_FONT_ID;
    this.maxChars = 200;
    
    // Resilience state
    this.isSaving = false;
    this.lastSubmitTime = 0;
    this.submitDebounceMs = 400;
    this.saveTimeoutMs = 10000;
    this.previouslyFocusedElement = null;
    this._savedScrollY = 0;
    this._baseViewportHeight = 0;
    this._inertTarget = null;
    this._scrollCleanup = null;

    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.switchPreviewSide = this.switchPreviewSide.bind(this);
    this.handleCardChange = this.handleCardChange.bind(this);
    this.handleRecipientInput = this.handleRecipientInput.bind(this);
    this.handleNoteInput = this.handleNoteInput.bind(this);
    this.handleSenderInput = this.handleSenderInput.bind(this);
    this.handleFontChange = this.handleFontChange.bind(this);
    this.handleGreetingChange = (e) => this.handlePresetChange('greeting', e);
    this.handleClosingChange = (e) => this.handlePresetChange('closing', e);
    this.confirmCard = this.confirmCard.bind(this);
    this.changeCard = this.changeCard.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleViewportResize = this.handleViewportResize.bind(this);
  }
  
  connectedCallback() {
    this.render();
    this.attachEventListeners();

    // Persistent image node for the summary icon — created once, updated in updatePreview().
    this._summaryImg = document.createElement('img');
    this._summaryImg.className = 'gift-modal__card-summary-img';
  }
  
  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    this._scrollCleanup?.();
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportResize);
      window.visualViewport.removeEventListener('scroll', this.handleViewportResize);
    }
  }

  // Centralized cleanup for all inline styles set by handleViewportResize.
  // Called from open(), close(), and the keyboard-closed branch.
  _clearKeyboardStyles() {
    const overlay = this.querySelector('#gift-modal-overlay');
    const modal = this.querySelector('.gift-modal');
    const body = this.querySelector('.gift-modal__body');
    const footer = this.querySelector('.gift-modal__footer');
    if (overlay) overlay.style.transform = '';
    if (modal) modal.style.height = '';
    if (body) body.style.paddingBottom = '';
    if (footer) footer.style.display = '';
  }
  
  render() {
    const defaultCard = CARD_CATALOG_BY_ID[DEFAULT_CARD_ID] || CARD_PICKER_CATALOG[0] || CARD_CATALOG[0];

    const cardOptionsHtml = CARD_PICKER_CATALOG.map((card) => {
      const visualHtml = card.imageUrl
        ? `<img class="gift-modal__cover-img" ${getCardImageAttributes(card, 'cover')} data-card-image-id="${card.id}" data-card-image-variant="cover" alt="${card.label}">`
        : '';
      return `
      <label class="gift-modal__cover-option">
        <input type="radio" name="cardId" value="${card.id}" ${card.id === DEFAULT_CARD_ID ? 'checked' : ''}>
        <div class="gift-modal__cover-label${card.imageUrl ? ' has-art' : ''}" data-card-id="${card.id}">
          ${visualHtml}
          <span class="gift-modal__cover-name">${card.label}</span>
          <span class="gift-modal__cover-greeting">${(card.greetingPresets && card.greetingPresets[0]) || ''}</span>
        </div>
      </label>`;
    }).join('');

    const fontOptionsHtml = FONT_OPTIONS.map((font) => `
      <label class="gift-modal__font-option">
        <input type="radio" name="font" value="${font.id}" ${font.id === DEFAULT_FONT_ID ? 'checked' : ''}>
        <div class="gift-modal__font-label">
          <span class="gift-modal__font-preview ${font.previewClass}">Aa</span>
          <span class="gift-modal__font-name">${font.label}</span>
        </div>
      </label>
    `).join('');

    this.innerHTML = `
      <div class="gift-modal-overlay" id="gift-modal-overlay">
        <div class="gift-modal" role="dialog" aria-modal="true" aria-labelledby="gift-modal-title">

          <!-- Header -->
          <div class="gift-modal__header">
            <h2 class="gift-modal__title" id="gift-modal-title">
              <span class="gift-modal__title-icon">🎁</span>
              <span class="gift-modal__title-text">Add a gift note</span>
            </h2>
            <span class="gift-modal__step-indicator" id="step-indicator">Step 1 of 2</span>
            <button type="button" class="gift-modal__close" aria-label="Close" id="gift-modal-close">
              ✕
            </button>
          </div>

          <!-- Body -->
          <div class="gift-modal__body">

            <!-- Left: Card Preview -->
            <div class="gift-modal__preview-area">

              <!-- Mobile Segmented Control -->
              <div class="gift-modal__tabs" role="tablist" aria-label="Card preview">
                <button
                  type="button"
                  class="gift-modal__tab is-active"
                  role="tab"
                  aria-selected="true"
                  aria-controls="preview-front-panel"
                  data-preview-tab="front"
                  id="tab-front"
                >
                  Front
                </button>
                <button
                  type="button"
                  class="gift-modal__tab"
                  role="tab"
                  aria-selected="false"
                  aria-controls="preview-back-panel"
                  data-preview-tab="back"
                  id="tab-back"
                >
                  Back
                </button>
              </div>

              <!-- Two-Up Preview (both visible on desktop, one at a time on mobile) -->
              <div class="gift-modal__previews">

                <!-- Front Preview (readonly) -->
                <div
                  class="gift-modal__preview-card gift-modal__preview-front is-active"
                  id="preview-front-panel"
                  role="tabpanel"
                  aria-labelledby="tab-front"
                  data-preview-panel="front"
                >
                  <div class="gift-modal__preview-label">Front</div>
                  <div class="gift-modal__card-face gift-modal__card-front is-${DEFAULT_CARD_ID}" id="card-front">
                    <div class="gift-modal__card-front-content">
                      <img class="gift-modal__card-front-art" id="front-art"
                        ${getCardImageAttributes(defaultCard, 'front')} alt="${defaultCard.label}"
                        ${defaultCard.imageUrl ? '' : 'hidden'}
                        style="${defaultCard.imageStyle ? Object.entries(defaultCard.imageStyle).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}:${v}`).join(';') : ''}">
                      <div class="gift-modal__card-front-greeting" id="front-greeting">${defaultCard.greetingPresets[0]}</div>
                      <div class="gift-modal__card-front-subtext" id="front-closing">${defaultCard.closingPresets[0]}</div>
                    </div>
                  </div>
                </div>

                <!-- Back Preview (read-only) -->
                <div
                  class="gift-modal__preview-card gift-modal__preview-back"
                  id="preview-back-panel"
                  role="tabpanel"
                  aria-labelledby="tab-back"
                  data-preview-panel="back"
                >
                  <div class="gift-modal__preview-label">Back</div>
                  <div class="gift-modal__card-face gift-modal__card-back" id="card-back">
                    <div class="gift-modal__card-back-content">
                      <div class="gift-modal__card-recipient">
                        To: <span class="gift-modal__card-recipient-name" id="preview-recipient">Someone Special</span>
                      </div>
                      <div class="gift-modal__card-message is-serif" id="preview-message">
                        <span class="gift-modal__card-message-placeholder">Your message will show up here...</span>
                      </div>
                      <div class="gift-modal__card-signature" id="preview-sender">
                        — With love
                      </div>
                    </div>
                  </div>
                </div>

              </div>
              <p class="gift-modal__illustrator-credit" id="illustrator-credit"${defaultCard.artist ? '' : ' hidden'}>${defaultCard.artist ? `Illustrated by ${defaultCard.artist.url ? `<a href="${defaultCard.artist.url}" target="_blank" rel="noopener" class="gift-modal__illustrator-link">${defaultCard.artist.name}</a>` : defaultCard.artist.name}` : ''}</p>
            </div>

            <!-- Right: Editor Sidebar -->
            <div class="gift-modal__editor" aria-live="polite">
              <div class="gift-modal__editor-scroll">

                <!-- ======= STEP 1: Pick your card ======= -->
                <div class="gift-modal__step gift-modal__step-card is-active-step" data-step="pick-card">
                  <div class="gift-modal__section-header">
                    <h3 class="gift-modal__section-title gift-modal__section-title--step">Pick your card</h3>
                  </div>
                  <p class="gift-modal__helper-copy">Each card is hand-illustrated. You can always change it later.</p>

                  <div class="gift-modal__covers" data-covers role="radiogroup" aria-label="Card design">
                    ${cardOptionsHtml}
                  </div>
                </div>

                <!-- ======= STEP 2: Write your message ======= -->
                <div class="gift-modal__step gift-modal__step-message" data-step="write-message">
                  <!-- Summary row (collapsed card choice) -->
                  <button type="button" class="gift-modal__card-summary" id="card-summary" aria-label="${defaultCard.label} card selected. Change card choice">
                    <span class="gift-modal__card-summary-visual" id="summary-icon">${defaultCard.imageUrl ? `<img class="gift-modal__card-summary-img" ${getCardImageAttributes(defaultCard, 'summary')} alt="${defaultCard.label}">` : ''}</span>
                    <span class="gift-modal__card-summary-text">
                      <span id="summary-label">${defaultCard.label}</span>
                    </span>
                    <span class="gift-modal__card-summary-action">Change</span>
                  </button>

                  <!-- ── Font ── -->
                  <div class="gift-modal__section">
                    <label class="gift-modal__field-label">Font</label>
                    <div class="gift-modal__fonts" role="radiogroup" aria-label="Font style">
                      ${fontOptionsHtml}
                    </div>
                  </div>

                  <!-- ── Card front ── -->
                  <div class="gift-modal__section">
                    <h3 class="gift-modal__section-divider">Card front</h3>

                    <label class="gift-modal__field-label">Headline</label>
                    <div class="gift-modal__preset-chips" data-preset-group="greeting"></div>

                    <label class="gift-modal__field-label gift-modal__field-spacer">Accent line</label>
                    <div class="gift-modal__preset-chips" data-preset-group="closing"></div>
                  </div>

                  <!-- ── Card back ── -->
                  <div class="gift-modal__section">
                    <h3 class="gift-modal__section-divider">Card back</h3>

                    <div class="gift-modal__input-group">
                      <label class="gift-modal__field-label" for="recipient-name">To</label>
                      <input
                        type="text"
                        class="gift-modal__input"
                        id="recipient-name"
                        placeholder="e.g. Mom"
                        maxlength="50"
                      >
                    </div>

                    <div class="gift-modal__input-group gift-modal__field-spacer">
                      <label class="gift-modal__field-label" for="gift-note">Message</label>
                      <div class="gift-modal__note-wrapper">
                        <textarea
                          class="gift-modal__note"
                          id="gift-note"
                          placeholder="Write something meaningful..."
                          maxlength="${this.maxChars}"
                          aria-describedby="char-count"
                        ></textarea>
                        <div class="gift-modal__char-count" id="char-count" aria-live="polite">0/${this.maxChars}</div>
                      </div>
                    </div>

                    <div class="gift-modal__input-group gift-modal__field-spacer">
                      <label class="gift-modal__field-label" for="sender-name">From <span class="gift-modal__label-hint">(optional)</span></label>
                      <input
                        type="text"
                        class="gift-modal__input"
                        id="sender-name"
                        placeholder="e.g. Leo"
                        maxlength="50"
                      >
                    </div>
                  </div>
                </div>

              </div>

              <!-- Footer Actions -->
              <div class="gift-modal__footer">
                <button type="button" class="gift-modal__btn gift-modal__btn-cancel" id="gift-modal-cancel">
                  Close
                </button>
                <button type="button" class="gift-modal__btn gift-modal__btn-submit" id="gift-modal-submit">
                  <span id="submit-btn-text">Write your message</span>
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>
    `;

    // Populate greeting/closing preset chips for the default card
    this.renderPresetChips();
  }

  hydrateCardImages() {
    this.imagesHydrated = true;
    this.querySelectorAll('[data-card-image-id][data-card-image-variant]').forEach((img) => {
      const card = CARD_CATALOG_BY_ID[img.dataset.cardImageId];
      applyCardImage(img, card, img.dataset.cardImageVariant);
    });
    this.updatePreview();
  }

  renderPresetChips() {
    const card = CARD_CATALOG_BY_ID[this.selectedCardId] || CARD_CATALOG[0];

    // Auto-select first preset if nothing selected yet
    if (!this.selectedGreeting && card.greetingPresets?.length) {
      this.selectedGreeting = card.greetingPresets[0];
    }
    if (!this.selectedClosing && card.closingPresets?.length) {
      this.selectedClosing = card.closingPresets[0];
    }

    // Determine if current selection is custom (not in presets)
    const greetingIsCustom = this.selectedGreeting && !(card.greetingPresets || []).includes(this.selectedGreeting);
    const closingIsCustom = this.selectedClosing && !(card.closingPresets || []).includes(this.selectedClosing);

    const greetingContainer = this.querySelector('[data-preset-group="greeting"]');
    if (greetingContainer) {
      greetingContainer.innerHTML = (card.greetingPresets || []).map((text) => {
        const safeAttr = this.escapeAttr(text);
        const safeText = this.escapeHtml(text);
        const isSelected = !greetingIsCustom && this.selectedGreeting === text;
        return `
        <button type="button" class="gift-modal__chip${isSelected ? ' is-selected' : ''}"
          data-preset="greeting" data-value="${safeAttr}" aria-label="Headline: ${safeAttr}"
          aria-pressed="${isSelected ? 'true' : 'false'}">
          ${safeText}
        </button>
      `;
      }).join('') + `
        <button type="button" class="gift-modal__chip gift-modal__chip--custom${greetingIsCustom ? ' is-selected' : ''}"
          data-preset="greeting" data-value="__custom__"
          aria-pressed="${greetingIsCustom ? 'true' : 'false'}">
          Custom\u2026
        </button>
        <div class="gift-modal__custom-input-wrap${greetingIsCustom ? ' is-visible' : ''}" data-custom-wrap="greeting">
          <input type="text" class="gift-modal__custom-input" data-custom-input="greeting"
            placeholder="Your headline" maxlength="${GREETING_MAX_LENGTH}"
            aria-label="Custom headline"
            value="${greetingIsCustom ? this.escapeAttr(this.selectedGreeting) : ''}">
          <span class="gift-modal__custom-count" data-custom-count="greeting">${greetingIsCustom ? this.selectedGreeting.length : 0}/${GREETING_MAX_LENGTH}</span>
        </div>
      `;
    }

    const closingContainer = this.querySelector('[data-preset-group="closing"]');
    if (closingContainer) {
      closingContainer.innerHTML = (card.closingPresets || []).map((text) => {
        const safeAttr = this.escapeAttr(text);
        const safeText = this.escapeHtml(text);
        const isSelected = !closingIsCustom && this.selectedClosing === text;
        return `
        <button type="button" class="gift-modal__chip${isSelected ? ' is-selected' : ''}"
          data-preset="closing" data-value="${safeAttr}" aria-label="Accent line: ${safeAttr}"
          aria-pressed="${isSelected ? 'true' : 'false'}">
          ${safeText}
        </button>
      `;
      }).join('') + `
        <button type="button" class="gift-modal__chip gift-modal__chip--custom${closingIsCustom ? ' is-selected' : ''}"
          data-preset="closing" data-value="__custom__"
          aria-pressed="${closingIsCustom ? 'true' : 'false'}">
          Custom\u2026
        </button>
        <div class="gift-modal__custom-input-wrap${closingIsCustom ? ' is-visible' : ''}" data-custom-wrap="closing">
          <input type="text" class="gift-modal__custom-input" data-custom-input="closing"
            placeholder="Your accent line" maxlength="${CLOSING_MAX_LENGTH}"
            aria-label="Custom accent line"
            value="${closingIsCustom ? this.escapeAttr(this.selectedClosing) : ''}">
          <span class="gift-modal__custom-count" data-custom-count="closing">${closingIsCustom ? this.selectedClosing.length : 0}/${CLOSING_MAX_LENGTH}</span>
        </div>
      `;
    }

    // Attach input listeners for custom text fields
    this.querySelector('[data-custom-input="greeting"]')?.addEventListener('input', (e) => {
      this.selectedGreeting = e.target.value;
      const countEl = this.querySelector('[data-custom-count="greeting"]');
      if (countEl) countEl.textContent = `${e.target.value.length}/${GREETING_MAX_LENGTH}`;
      this.updatePreview();
    });
    this.querySelector('[data-custom-input="closing"]')?.addEventListener('input', (e) => {
      this.selectedClosing = e.target.value;
      const countEl = this.querySelector('[data-custom-count="closing"]');
      if (countEl) countEl.textContent = `${e.target.value.length}/${CLOSING_MAX_LENGTH}`;
      this.updatePreview();
    });

    this.updatePreview();
  }

  setStep(stepName) {
    this.step = stepName;

    // Toggle visibility with crossfade
    this.querySelectorAll('.gift-modal__step').forEach((el) => {
      el.classList.toggle('is-active-step', el.dataset.step === stepName);
    });

    // Reset scroll position so the new step starts from the top
    const scrollContainer = this.querySelector('.gift-modal__editor-scroll');
    if (scrollContainer) scrollContainer.scrollTop = 0;

    // Update footer CTA text
    const submitText = this.querySelector('#submit-btn-text');
    if (submitText) {
      if (stepName === 'pick-card') {
        submitText.textContent = 'Write your message';
      } else {
        submitText.textContent = this.mode === 'edit' ? 'Save changes' : 'Add card';
      }
    }

    // Update cancel button text
    const cancelBtn = this.querySelector('#gift-modal-cancel');
    if (cancelBtn) {
      cancelBtn.textContent = 'Close';
    }

    // Update step indicator
    const indicator = this.querySelector('#step-indicator');
    if (indicator) {
      indicator.textContent = stepName === 'pick-card' ? 'Step 1 of 2' : 'Step 2 of 2';
    }
  }

  confirmCard() {
    this.setStep('write-message');
    // Re-render preset chips for the confirmed card
    this.renderPresetChips();
    // Reset scroll after DOM settles (renderPresetChips triggers layout)
    requestAnimationFrame(() => {
      const scrollContainer = this.querySelector('.gift-modal__editor-scroll');
      if (scrollContainer) scrollContainer.scrollTop = 0;
    });
  }

  changeCard() {
    this.setStep('pick-card');
  }

  handlePresetChange(type, e) {
    const btn = e.target.closest(`.gift-modal__chip[data-preset="${type}"]`);
    if (!btn) return;

    const stateKey = type === 'greeting' ? 'selectedGreeting' : 'selectedClosing';
    const customWrap = this.querySelector(`[data-custom-wrap="${type}"]`);
    const customInput = this.querySelector(`[data-custom-input="${type}"]`);

    if (btn.dataset.value === '__custom__') {
      if (customWrap) customWrap.classList.add('is-visible');
      this[stateKey] = customInput?.value || '';
      if (customInput) setTimeout(() => customInput.focus(), 50);
    } else {
      this[stateKey] = btn.dataset.value;
      if (customWrap) customWrap.classList.remove('is-visible');
    }

    this.querySelectorAll(`.gift-modal__chip[data-preset="${type}"]`).forEach((chip) => {
      let selected;
      if (chip.dataset.value === '__custom__') {
        selected = btn.dataset.value === '__custom__';
      } else {
        selected = btn.dataset.value !== '__custom__' && chip.dataset.value === this[stateKey];
      }
      chip.classList.toggle('is-selected', selected);
      chip.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    this.updatePreview();
  }

  attachEventListeners() {
    this.querySelector('#gift-modal-close').addEventListener('click', () => this.close());
    this.querySelector('#gift-modal-cancel').addEventListener('click', () => this.close());
    
    this.querySelector('#gift-modal-overlay').addEventListener('click', (e) => {
      if (e.target.id === 'gift-modal-overlay') {
        this.close();
      }
    });
    
    // Mobile segmented control (tabs)
    this.querySelectorAll('[data-preview-tab]').forEach(tab => {
      tab.addEventListener('click', (e) => {
        const side = e.target.dataset.previewTab;
        this.switchPreviewSide(side);
      });
    });
    
    // Card selection
    this.querySelectorAll('input[name="cardId"]').forEach(input => {
      input.addEventListener('change', this.handleCardChange);
    });

    // Summary row (Step 2 → back to Step 1)
    const summaryBtn = this.querySelector('#card-summary');
    if (summaryBtn) {
      summaryBtn.addEventListener('click', this.changeCard);
    }

    // Greeting/closing preset chips (event delegation on containers)
    const greetingContainer = this.querySelector('[data-preset-group="greeting"]');
    if (greetingContainer) {
      greetingContainer.addEventListener('click', this.handleGreetingChange);
    }
    const closingContainer = this.querySelector('[data-preset-group="closing"]');
    if (closingContainer) {
      closingContainer.addEventListener('click', this.handleClosingChange);
    }

    // Recipient input
    this.querySelector('#recipient-name').addEventListener('input', this.handleRecipientInput);
    
    // Note input
    this.querySelector('#gift-note').addEventListener('input', this.handleNoteInput);
    
    // Sender input
    this.querySelector('#sender-name').addEventListener('input', this.handleSenderInput);
    
    // Font selection
    this.querySelectorAll('input[name="font"]').forEach(input => {
      input.addEventListener('change', this.handleFontChange);
    });
    
    // Submit
    this.querySelector('#gift-modal-submit').addEventListener('click', this.handleSubmit);
    
    // Keyboard
    document.addEventListener('keydown', this.handleKeydown);
    
    // iOS keyboard handling — wait for viewport to settle, then scroll
    const editorScroll = this.querySelector('.gift-modal__editor-scroll');
    if (editorScroll) {
      editorScroll.addEventListener('focusin', (e) => {
        if (!e.target.matches('input, textarea, select')) return;
        this._scrollToInput(e.target);
      });
    }
    
    // Visual viewport handling for iOS Safari keyboard.
    // `resize` fires when the keyboard opens/closes (height changes).
    // `scroll` fires when iOS shifts the visual viewport (offsetTop changes).
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleViewportResize);
      window.visualViewport.addEventListener('scroll', this.handleViewportResize);
    }
  }
  
  handleKeydown(e) {
    if (!this.isOpen) return;
    
    // Escape to close (unless saving)
    if (e.key === 'Escape') {
      if (this.isSaving) return;  // Don't interfere during save
      e.preventDefault();
      this.close();
      return;
    }
    
    // Tab trap - keep focus within modal
    if (e.key === 'Tab') {
      const modal = this.querySelector('.gift-modal');
      if (!modal) return;
      
      const allFocusable = modal.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const focusableElements = [...allFocusable].filter((el) => {
        if (el.hidden || el.closest('[hidden]')) return false;
        if (el.offsetParent === null && !el.closest('[role="dialog"]')) return false;
        const step = el.closest('.gift-modal__step');
        if (step && !step.classList.contains('is-active-step')) return false;
        return true;
      });

      if (focusableElements.length === 0) return;

      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];
      
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  }
  
  handleViewportResize() {
    if (!this.isOpen || !window.visualViewport) return;
    const overlay = this.querySelector('#gift-modal-overlay');
    const modal = this.querySelector('.gift-modal');
    if (!overlay || !modal) return;

    const vv = window.visualViewport;
    // 60px threshold (Vaul pattern): address bar collapse is ~44px,
    // keyboard is ~250-350px. This avoids false positives.
    const diff = (this._baseViewportHeight || window.innerHeight) - vv.height;

    if (diff > 60) {
      // Keyboard is open.
      // Translate the overlay so it tracks the visual viewport, but keep
      // its height at 100% so the dark backdrop covers the full screen
      // (the keyboard itself hides what's below the modal).
      overlay.style.transform = `translateY(${vv.offsetTop}px)`;
      modal.style.height = `${vv.height}px`;

      // iOS contentInset equivalent: add bottom padding so the last field
      // (e.g. "From") can scroll into view above the keyboard — same
      // pattern as UIKit's scrollView.contentInset.bottom = keyboardHeight.
      const body = this.querySelector('.gift-modal__body');
      if (body) body.style.paddingBottom = `${diff}px`;

      // Hide the sticky footer while the keyboard is up — it eats ~70px
      // of the already cramped visible area.  Close/Save aren't needed
      // while typing; they reappear the moment the keyboard closes.
      const footer = this.querySelector('.gift-modal__footer');
      if (footer) footer.style.display = 'none';
    } else {
      // Keyboard closed — let CSS (100dvh) handle address-bar changes.
      this._clearKeyboardStyles();
      this._baseViewportHeight = vv.height;
    }
  }

  // Scroll the focused input into view *within the modal's own scroll
  // container* (`.gift-modal__body` on mobile).  Using the browser's
  // `scrollIntoView()` on iOS causes the page to jump because the
  // overlay is `position: fixed`.  Instead we calculate the input's
  // offset relative to the scroll container and scroll that container
  // directly.  If no scroll container is found (desktop sidebar layout),
  // we fall back to a no-op because desktop doesn't need this help.
  // ── iOS keyboard scroll fix ──
  // HARD-WON LESSON (Feb 2026, ~6 iterations):
  //
  // Problem: On iOS Safari/Chrome, tapping a form field (especially "From",
  // the last field) in a position:fixed modal doesn't reliably scroll it
  // into view when the software keyboard opens.
  //
  // What DIDN'T work:
  //  1. Manual getBoundingClientRect + scrollTop math — too many timing
  //     failure modes during the ~300ms iOS keyboard animation. Measurements
  //     taken mid-animation are stale by the time scrollTop is set.
  //  2. 100dvh for modal height — dvh does NOT track the iOS keyboard,
  //     only visualViewport.height does.
  //  3. position:fixed + overflow:hidden for scroll lock — WebKit bug
  //     #153852 means hidden still allows scroll. Need overflow:clip.
  //
  // What DOES work:
  //  - overflow:clip on html/body (via .gift-scroll-locked) prevents page
  //    scroll entirely, so scrollIntoView can only scroll within
  //    .gift-modal__body — no page jump risk.
  //  - visualViewport resize listener with 300ms debounce (matches keyboard
  //    animation duration) ensures we scroll AFTER layout is final.
  //  - Hide the sticky footer when keyboard is open (saves ~70px of space).
  //  - body.style.paddingBottom = keyboardHeight gives the scroll container
  //    enough room to scroll the last field into view (iOS contentInset
  //    equivalent).
  //
  // See also: ai/skills/remember/lessons/ios-keyboard-scroll.md
  _scrollToInput(input) {
    if (!input || !input.isConnected) return;

    // Cancel any in-flight scroll from a prior focusin (M2/M3: prevents
    // listener leak and race condition on rapid focus switching).
    this._scrollCleanup?.();

    // Use the browser's native scrollIntoView instead of manual
    // getBoundingClientRect math.  With overflow:clip on html/body
    // (gift-scroll-locked), scrollIntoView can only scroll within
    // .gift-modal__body — no page jump risk.
    const doScroll = () => {
      if (!input.isConnected) return;
      input.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };

    // Wait for the iOS keyboard animation to fully settle before scrolling.
    // The keyboard takes ~250-300ms to animate; we debounce resize events
    // and fire after 300ms of quiet to ensure layout is final.
    const vv = window.visualViewport;
    if (!vv) {
      setTimeout(doScroll, 300);
      return;
    }

    let didScroll = false;
    let sawResize = false;
    let settleTimer;
    let fallbackTimer;

    const cleanup = () => {
      vv.removeEventListener('resize', onResize);
      clearTimeout(settleTimer);
      clearTimeout(fallbackTimer);
      this._scrollCleanup = null;
    };

    const scrollOnce = () => {
      if (didScroll) return;
      didScroll = true;
      cleanup();
      requestAnimationFrame(doScroll);
    };

    const onResize = () => {
      sawResize = true;
      clearTimeout(settleTimer);
      // 300ms settle: matches iOS keyboard animation duration (~250-300ms)
      settleTimer = setTimeout(scrollOnce, 300);
    };

    vv.addEventListener('resize', onResize);
    this._scrollCleanup = cleanup;

    // If the keyboard was already open (e.g. tabbing between fields),
    // no resize fires. Scroll after a short delay.
    fallbackTimer = setTimeout(() => {
      if (!sawResize) scrollOnce();
    }, 400);
  }

  // Mobile segmented control - switch between front/back preview
  switchPreviewSide(side) {
    this.activePreviewSide = side;
    
    // Update tabs
    this.querySelectorAll('[data-preview-tab]').forEach(tab => {
      const isActive = tab.dataset.previewTab === side;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    
    // Update panels (cross-fade 180ms via CSS)
    this.querySelectorAll('[data-preview-panel]').forEach(panel => {
      const isActive = panel.dataset.previewPanel === side;
      panel.classList.toggle('is-active', isActive);
    });
  }
  
  handleCardChange(e) {
    this.selectedCardId = e.target.value;
    // Reset front-of-card selections to new card's defaults
    const card = CARD_CATALOG_BY_ID[this.selectedCardId];
    this.selectedGreeting = card?.greetingPresets?.[0] || '';
    this.selectedClosing = card?.closingPresets?.[0] || '';
    this.renderPresetChips();
    this.updateIllustratorCredit(card);
  }

  updateIllustratorCredit(card) {
    const el = this.querySelector('#illustrator-credit');
    if (!el) return;
    if (!card?.artist) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = 'Illustrated by ';
    if (card.artist.url) {
      const link = document.createElement('a');
      link.href = card.artist.url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'gift-modal__illustrator-link';
      link.textContent = card.artist.name;
      el.appendChild(link);
    } else {
      el.appendChild(document.createTextNode(card.artist.name));
    }
  }

  handleRecipientInput(e) {
    this.recipientName = e.target.value;
    this.updatePreview();
  }
  
  handleNoteInput(e) {
    this.noteText = e.target.value;
    this.updateCharCount();
    this.updatePreview();
    if (this.callerTrigger?.updateState) {
      this.callerTrigger.updateState();
    } else {
      Gift.updateGiftNoteCardState?.();
    }
  }
  
  handleSenderInput(e) {
    this.senderName = e.target.value;
    this.updatePreview();
  }
  
  handleFontChange(e) {
    this.selectedFont = normalizeFontId(e.target.value);
    this.updatePreview();
  }
  
  updateCharCount() {
    const count = this.noteText.length;
    const remaining = this.maxChars - count;
    const charCountEl = this.querySelector('#char-count');
    
    if (charCountEl) {
      charCountEl.textContent = `${count}/${this.maxChars}`;
      charCountEl.classList.remove('is-near-limit', 'is-at-limit');
      if (remaining <= 0) {
        charCountEl.classList.add('is-at-limit');
      } else if (remaining <= this.maxChars * 0.2) {
        charCountEl.classList.add('is-near-limit');
      }
    }
  }
  
  updatePreview() {
    // Font class helper — must be defined before first use
    const fontClasses = ['is-modern', 'is-serif'];
    const applyFontClass = (el) => {
      if (!el) return;
      el.classList.remove(...fontClasses);
      el.classList.add(`is-${this.selectedFont}`);
    };

    // Update front card
    const frontCard = this.querySelector('#card-front');
    const frontGreeting = this.querySelector('#front-greeting');
    const frontSubtext = this.querySelector('.gift-modal__card-front-subtext');
    if (!CARD_CATALOG_BY_ID[this.selectedCardId]) {
      this.selectedCardId = DEFAULT_CARD_ID;
      const fallbackInput =
        this.querySelector(`input[name="cardId"][value="${this.selectedCardId}"]`);
      if (fallbackInput) fallbackInput.checked = true;
    }
    const card = CARD_CATALOG_BY_ID[this.selectedCardId];

    if (frontCard) {
      frontCard.classList.forEach((className) => {
        if (className.startsWith('is-')) frontCard.classList.remove(className);
      });
      frontCard.classList.add(`is-${this.selectedCardId}`);
    }

    // Handle artwork image
    const frontArt = this.querySelector('#front-art');
    if (frontArt) {
      if (card?.imageUrl) {
        if (this.imagesHydrated) applyCardImage(frontArt, card, 'front');
        else applyDeferredCardImage(frontArt, card, 'front');
        frontArt.alt = card.label || '';
        frontArt.hidden = false;
        // Apply per-card image sizing
        frontArt.style.maxHeight = card.imageStyle?.maxHeight || '';
        frontArt.style.marginTop = card.imageStyle?.marginTop || '';
      } else {
        frontArt.hidden = true;
      }
    }
    if (frontGreeting) {
      frontGreeting.textContent = this.selectedGreeting || card?.greetingPresets?.[0] || '';
      applyFontClass(frontGreeting);
    }
    if (frontSubtext) {
      frontSubtext.textContent = this.selectedClosing || card?.closingPresets?.[0] || '';
      applyFontClass(frontSubtext);
    }

    // Update summary row (Step 2 header)
    const summaryIcon = this.querySelector('#summary-icon');
    const summaryLabel = this.querySelector('#summary-label');

    if (summaryIcon) {
      if (card?.imageUrl && this._summaryImg) {
        // Reuse persistent image node — just update src/alt instead of recreating.
        if (this.imagesHydrated) applyCardImage(this._summaryImg, card, 'summary');
        else applyDeferredCardImage(this._summaryImg, card, 'summary');
        this._summaryImg.alt = card.label || '';
        summaryIcon.replaceChildren(this._summaryImg);
      } else {
        summaryIcon.textContent = card?.label || '';
      }
    }
    if (summaryLabel && card?.label) summaryLabel.textContent = card.label;

    // Update summary row aria-label
    const summaryBtn = this.querySelector('#card-summary');
    if (summaryBtn && card?.label) {
      summaryBtn.setAttribute('aria-label', `${card.label} card selected. Change card choice`);
    }

    // Update recipient
    const recipientEl = this.querySelector('#preview-recipient');
    const recipientRow = recipientEl?.closest('.gift-modal__card-recipient');
    const trimmedRecipient = this.recipientName.trim();
    if (recipientEl) {
      recipientEl.textContent = trimmedRecipient;
      applyFontClass(recipientEl);
    }
    if (recipientRow) {
      recipientRow.hidden = !trimmedRecipient;
      applyFontClass(recipientRow);
    }
    
    // Update message
    const messageEl = this.querySelector('#preview-message');
    if (messageEl) {
      applyFontClass(messageEl);
      
      if (this.noteText.trim()) {
        messageEl.innerHTML = this.escapeHtml(this.noteText);
      } else {
        messageEl.innerHTML = '<span class="gift-modal__card-message-placeholder">Your message will show up here...</span>';
      }
    }
    
    // Update sender
    const senderEl = this.querySelector('#preview-sender');
    if (senderEl) {
      const trimmedSender = this.senderName.trim();
      if (trimmedSender) {
        senderEl.textContent = `— ${trimmedSender}`;
        senderEl.hidden = false;
      } else {
        senderEl.textContent = '';
        senderEl.hidden = true;
      }
      applyFontClass(senderEl);
    }
  }
  
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/\n/g, '<br>');
  }

  escapeAttr(text) {
    return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  
  // Validate form fields - returns error message or null if valid
  validateForm() {
    const message = this.noteText.trim();

    // Message length check
    if (message.length > this.maxChars) {
      return {
        field: 'gift-note',
        message: `Keep it under ${this.maxChars} characters — brevity is beautiful.`
      };
    }

    // Greeting length check
    if (this.selectedGreeting.length > GREETING_MAX_LENGTH) {
      return {
        field: null,
        message: `Headline is too long — keep it under ${GREETING_MAX_LENGTH} characters.`
      };
    }

    // Closing length check
    if (this.selectedClosing.length > CLOSING_MAX_LENGTH) {
      return {
        field: null,
        message: `Accent line is too long — keep it under ${CLOSING_MAX_LENGTH} characters.`
      };
    }

    // Card validation (guard against invalid state)
    const validCards = Object.keys(CARD_CATALOG_BY_ID);
    if (!validCards.includes(this.selectedCardId)) {
      return {
        field: null,
        message: 'Pick a card first.'
      };
    }

    return null;
  }
  
  handleSubmit() {
    // Step 1: "Next" advances to Step 2
    if (this.step === 'pick-card') {
      this.confirmCard();
      return;
    }

    // Debounce: prevent rapid double-taps
    const now = Date.now();
    if (now - this.lastSubmitTime < this.submitDebounceMs) {
      Gift.debug?.('Debounced double-submit');
      return;
    }
    this.lastSubmitTime = now;

    // In-flight lock: don't submit if already saving
    if (this.isSaving) {
      Gift.debug?.('Already saving, ignoring submit');
      return;
    }

    // Set saving lock and disable button BEFORE any async work (prevents TOCTOU double-click)
    this.isSaving = true;
    this.setSubmitButtonLoading(true);

    // Clear any previous errors
    this.clearError();

    // Validate
    const error = this.validateForm();
    if (error) {
      this.isSaving = false;
      this.setSubmitButtonLoading(false);
      this.showError(error.message);
      // Focus the problematic field if specified
      if (error.field) {
        const field = this.querySelector(`#${error.field}`);
        if (field) {
          field.focus({ preventScroll: true });
          this._scrollToInput(field);
        }
      }
      return;
    }

    // Fall back to default preset if custom greeting/closing is blank
    const card = CARD_CATALOG_BY_ID[this.selectedCardId] || CARD_CATALOG[0];
    const greeting = this.selectedGreeting.trim() || card.greetingPresets?.[0] || '';
    const closing = this.selectedClosing.trim() || card.closingPresets?.[0] || '';

    // Build config object per Operation Santa spec
    const config = {
      schema: 'snowcubes.giftCard.v1',
      cardId: this.selectedCardId,
      frontGreeting: greeting,
      frontClosing: closing,
      to: this.recipientName.trim(),
      from: this.senderName.trim(),
      message: this.noteText.trim(),
      font: this.selectedFont
    };
    
    const configJson = JSON.stringify(config);

    // Serialized-size guard: the per-field limits (message 200, to/from 50, …) can
    // still sum past the cart line-item property byte cap (GIFT_CONFIG_MAX_BYTES).
    // Without this the note is SILENTLY dropped — localStorage save + the /cart
    // property both reject the oversize value — while the toast says "added". Use
    // the same serializer as the save path so the limit stays single-source.
    if (Gift.serializeGiftConfig && !Gift.serializeGiftConfig(config).ok) {
      this.isSaving = false;
      this.setSubmitButtonLoading(false);
      this.showError('Your note is a touch too long to fit on the card — please shorten it a little.');
      return;
    }

    Gift.debug?.('Gift Box Config', Gift.summarizeGiftConfig?.(config) || config);
    
    // Save to localStorage (prototype mode) for PDP card state
    if (window.giftConfigPrototype?.save) {
      window.giftConfigPrototype.save(config);
      Gift.debug?.('[Prototype] Saved gift config', Gift.summarizeGiftConfig?.(config) || undefined);
    }

    // "Edit" can mean two things:
    // 1) Pre-cart edit from PDP (no itemKey/variantId) → update product form inputs only
    // 2) Cart edit from cart drawer (itemKey/variantId provided) → /cart/change.js
    const isCartEdit = this.mode === 'edit' && (this.itemKey || this.variantId);

    if (isCartEdit) {
      // Cart edit mode: update cart via /cart/change.js
      this.saveChangesToCart(config);
      return;
    }

    // Add / pre-cart edit: inject hidden inputs into product form
    this.isSaving = false;
    this.setSubmitButtonLoading(false);
    this.injectConfigToProductForm(configJson, config);

    // Capture trigger before close() clears it
    const trigger = this.callerTrigger;

    this.close();
    this.showFeedback(this.mode === 'edit' ? 'Gift note updated' : 'Gift note added');

    // Update card state — prefer trigger's scoped update, fall back to global
    if (trigger?.updateState) {
      trigger.updateState();
    } else {
      Gift.updateGiftNoteCardState?.();
    }
    
    // Dispatch event for any listeners
    this.dispatchEvent(new CustomEvent('gift-box-submit', {
      detail: { config, mode: this.mode },
      bubbles: true
    }));
  }
  
  injectConfigToProductForm(configJson, config) {
    let productForm;

    // Prefer trigger's scoped form resolution (OOP path)
    if (this.callerTrigger?.resolveProductForm) {
      productForm = this.callerTrigger.resolveProductForm();
      Gift.debug?.('Form resolved via trigger', {
        sectionId: this.callerTrigger.sectionId || null,
      });
    }

    // Fallback: global DOM query (backward compat for non-trigger callers like cart drawer)
    if (!productForm) {
      const variantSelect = document.querySelector('variant-selects, variant-radios');
      const section = variantSelect?.closest('section, .product, [data-section]');
      productForm =
        section?.querySelector('product-form form[action*="/cart/add"]') ||
        section?.querySelector('product-form form') ||
        document.querySelector('product-form form[action*="/cart/add"]');
    }

    if (!productForm) {
      console.error('Product form not found');
      return;
    }

    if (this.callerTrigger?.ensureGiftVariantSelected) {
      const variantResult = this.callerTrigger.ensureGiftVariantSelected();
      Gift.debug?.('Gift variant selection before form injection', {
        ok: !!variantResult?.ok,
        changed: !!variantResult?.changed,
        error: variantResult?.error || null,
        variantId: variantResult?.variant?.id || null,
      });
    }
    
    // Prefer core helper (single source of truth + unit-tested).
    if (Gift.upsertGiftInputs) {
      const result = Gift.upsertGiftInputs(productForm, config);
      if (!result.ok) {
        console.warn('Failed to serialize gift config for form:', result.error);
      }
    } else {
      // Fallback (shouldn't run when core is loaded).
      const upsertInput = (name, value) => {
        let input = productForm.querySelector(`input[name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          productForm.appendChild(input);
        }
        input.value = value;
      };

      upsertInput('properties[_GiftCardConfig]', configJson);
      upsertInput('properties[_GiftCardTo]', config.to || '');
      upsertInput('properties[_GiftCardFrom]', config.from || '');
      upsertInput('properties[_GiftCardMessage]', config.message || '');
    }

    Gift.debug?.('Injected gift config into product form', {
      action: productForm.getAttribute('action') || '',
      ...Gift.summarizeGiftConfig?.(config),
    });
  }
  
  // Cart operations are delegated to gift-modal-cart.js (window.SnowcubesGiftCart).
  // This thin wrapper preserves the class method API for handleSubmit.
  async saveChangesToCart(config) {
    const CartAPI = window.SnowcubesGiftCart;
    if (!CartAPI) {
      console.error('gift-modal-cart.js not loaded');
      this.isSaving = false;
      this.setSubmitButtonLoading(false);
      this.showError('Something went wrong. Please refresh and try again.');
      return;
    }

    this.isSaving = true;
    this.setSubmitButtonLoading(true);

    const modal = this;
    const result = await CartAPI.saveChangesToCart({
      itemKey: this.itemKey,
      variantId: this.variantId,
      config: config,
      timeoutMs: this.saveTimeoutMs,
      callbacks: {
        onLoadingStart: () => {},
        onLoadingEnd: () => {
          modal.isSaving = false;
          modal.setSubmitButtonLoading(false);
        },
        onError: (msg) => modal.showError(msg),
        onSuccess: () => {
          modal.close();
          modal.showFeedback('Gift note updated');
        },
      },
    });

    // Ensure lock is released even if callbacks didn't fire
    this.isSaving = false;
    this.setSubmitButtonLoading(false);
  }
  
  // Visual loading state on submit button
  setSubmitButtonLoading(isLoading) {
    const btn = this.querySelector('#gift-modal-submit');
    const text = this.querySelector('#submit-btn-text');
    if (!btn || !text) return;
    
    if (isLoading) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      text.dataset.originalText = text.textContent;
      text.textContent = 'Saving...';
    } else {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      text.textContent = text.dataset.originalText || 'Save changes';
    }
  }
  
  // Clear any existing error message
  clearError() {
    const existingError = this.querySelector('.gift-modal__error');
    if (existingError) existingError.remove();
  }
  
  // Show error - calm, warm styling that fits the brand
  showError(message) {
    this.clearError();

    const errorEl = document.createElement('div');
    errorEl.className = 'gift-modal__error';
    errorEl.setAttribute('role', 'alert');
    errorEl.setAttribute('aria-live', 'polite');
    errorEl.textContent = message;
    this.querySelector('.gift-modal').appendChild(errorEl);

    // Auto-dismiss after reading time (but keep visible longer for network errors)
    const dismissTime = message.length > 60 ? 6000 : 4000;
    setTimeout(() => {
      if (errorEl.parentNode) {
        errorEl.classList.add('gift-modal__error--dismissing');
        setTimeout(() => errorEl.remove(), 300);
      }
    }, dismissTime);
  }
  
  showFeedback(message) {
    const toast = document.createElement('div');
    toast.className = 'gift-modal__toast';
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('gift-modal__toast--dismissing');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }
  
  // Push current state into DOM inputs (radios, text fields)
  syncFormToState() {
    const cardInput = this.querySelector(`input[name="cardId"][value="${this.selectedCardId}"]`);
    if (cardInput) cardInput.checked = true;

    const recipientEl = this.querySelector('#recipient-name');
    const noteEl = this.querySelector('#gift-note');
    const senderEl = this.querySelector('#sender-name');
    if (recipientEl) recipientEl.value = this.recipientName;
    if (noteEl) noteEl.value = this.noteText;
    if (senderEl) senderEl.value = this.senderName;

    const fontInput = this.querySelector(`input[name="font"][value="${this.selectedFont}"]`);
    if (fontInput) fontInput.checked = true;
  }

  open(options = {}) {
    this.mode = options.mode || 'add';
    this.variantId = options.variantId || null;
    this.itemKey = options.itemKey || null;
    this.callerTrigger = options.trigger || null;

    // A11y: Save currently focused element to restore on close
    this.previouslyFocusedElement = document.activeElement;
    
    // Reset any previous state
    this.isSaving = false;
    this.setSubmitButtonLoading(false);
    this.clearError();
    
    // Reset preview to front side (for mobile segmented control)
    this.switchPreviewSide('front');
    
    // Clear stale inline styles from a previous keyboard cycle
    this._clearKeyboardStyles();
    this._scrollCleanup?.();

    // Capture base viewport height before keyboard opens (for threshold calc).
    // Use innerHeight (layout viewport) — visualViewport.height could be
    // keyboard-reduced if opened while another field was focused.
    this._baseViewportHeight = window.innerHeight;

    // Set body flag for chat widget hiding etc.
    document.body.setAttribute('data-gift-modal-open', 'true');
    
    const titleText = this.querySelector('.gift-modal__title-text');
    titleText.textContent = this.mode === 'edit' ? 'Edit message' : 'Add a gift note';

    // Populate state from config / prefill / defaults
    if (options.config) {
      this.selectedCardId = CARD_CATALOG_BY_ID[options.config.cardId]
        ? options.config.cardId
        : DEFAULT_CARD_ID;
      this.selectedGreeting = options.config.frontGreeting || '';
      this.selectedClosing = options.config.frontClosing || '';
      this.recipientName = options.config.to || '';
      this.noteText = options.config.message || '';
      this.senderName = options.config.from || '';
      this.selectedFont = normalizeFontId(options.config.font);
    } else if (options.prefill) {
      const prefillCardId = options.prefill.cardId || options.prefill.cover;
      this.selectedCardId = CARD_CATALOG_BY_ID[prefillCardId]
        ? prefillCardId
        : DEFAULT_CARD_ID;
      this.selectedGreeting = '';
      this.selectedClosing = '';
      this.recipientName = options.prefill.recipient || '';
      this.noteText = options.prefill.note || '';
      this.senderName = options.prefill.sender || '';
      this.selectedFont = normalizeFontId(options.prefill.font);
    } else {
      this.selectedCardId = DEFAULT_CARD_ID;
      this.selectedGreeting = '';
      this.selectedClosing = '';
      this.recipientName = '';
      this.noteText = '';
      this.senderName = '';
      this.selectedFont = DEFAULT_FONT_ID;
    }

    this.syncFormToState();

    // Render preset chips (will auto-select defaults if greeting/closing empty)
    this.imagesHydrated = true;
    this.renderPresetChips();
    this.hydrateCardImages();
    this.updateCharCount();

    this.isOpen = true;
    this.querySelector('#gift-modal-overlay').classList.add('is-open');

    // Body scroll lock — overflow:clip approach.
    // The old `body { position: fixed; top: -scrollYpx }` pattern causes iOS
    // keyboard displacement: fixed-pos elements anchor to the layout viewport,
    // but the visual viewport shifts when the keyboard opens, making the modal
    // "ride up".  `overflow: clip` on html+body truly prevents all scrolling
    // (including iOS touch-through, unlike `overflow: hidden` — WebKit #153852)
    // without repositioning the body, so the keyboard has nothing to displace.
    // Supported in Safari 16+ (all 2026 iOS targets).
    this._savedScrollY = window.scrollY;
    document.documentElement.classList.add('gift-scroll-locked');

    // Mark background content as inert (a11y: prevents focus behind modal,
    // discourages iOS from scrolling background).
    const mainContent = document.getElementById('MainContent') || document.querySelector('main');
    if (mainContent) {
      mainContent.inert = true;
      this._inertTarget = mainContent;
    }

    // Route to correct step: add → Step 1, edit → Step 2
    if (this.mode === 'edit') {
      this.setStep('write-message');
      this.renderPresetChips();
      // Reset scroll after DOM settles
      requestAnimationFrame(() => {
        const scrollContainer = this.querySelector('.gift-modal__editor-scroll');
        if (scrollContainer) scrollContainer.scrollTop = 0;
      });
    } else {
      this.setStep('pick-card');
    }

    // A11y (WCAG 2.4.3): Move focus into the modal after it opens.
    // Use transitionend on the overlay to wait for visibility transition,
    // with a setTimeout fallback in case the event doesn't fire.
    const overlay = this.querySelector('#gift-modal-overlay');
    const focusModal = () => {
      if (!this.isOpen) return;
      const target = this.querySelector('.gift-modal__close');
      if (target) target.focus();
    };
    const onTransitionEnd = (e) => {
      if (e.propertyName === 'visibility' || e.propertyName === 'opacity') {
        overlay.removeEventListener('transitionend', onTransitionEnd);
        focusModal();
      }
    };
    if (overlay) overlay.addEventListener('transitionend', onTransitionEnd);
    // Fallback: focus after a generous delay if transitionend doesn't fire
    setTimeout(focusModal, 600);
  }
  
  close(force = false) {
    // Prevent close while saving (unless forced)
    if (this.isSaving && !force) {
      Gift.debug?.('Cannot close while saving');
      return;
    }
    
    this.isOpen = false;
    this.callerTrigger = null;
    this.querySelector('#gift-modal-overlay').classList.remove('is-open');

    // Clear inline styles from keyboard resize handler
    this._clearKeyboardStyles();
    this._scrollCleanup?.();

    // Restore body scroll — undo overflow:clip lock
    document.documentElement.classList.remove('gift-scroll-locked');
    if (this._inertTarget) {
      this._inertTarget.inert = false;
      this._inertTarget = null;
    }
    try {
      window.scrollTo(0, this._savedScrollY || 0);
    } catch {
      // Non-browser environments (e.g. JSDOM) may not implement scrollTo.
    }

    // Remove body flag
    document.body.removeAttribute('data-gift-modal-open');
    
    // A11y: Restore focus to previously focused element
    if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
      setTimeout(() => {
        this.previouslyFocusedElement.focus();
      }, 100);
    }
  }
}

customElements.define('gift-box-customizer-modal', GiftBoxCustomizerModal);

// Error + toast styles now live in gift-box-customizer-modal.css (no more CSS-in-JS).
// Global wiring (event delegation, DOMContentLoaded) lives in gift-modal-wiring.js.

  Gift.debug?.('Gift Box Customizer Modal class registered');
})();
